(() => {
  const configElement = document.getElementById('bb-delivery-config');
  if (!configElement) return;

  let config = {};
  try {
    config = JSON.parse(configElement.textContent);
  } catch (error) {
    return;
  }

  const timeZone = config.timezone || 'America/Toronto';
  const cutoffHour = Number(config.cutoffHour) || 10;
  const closedDates = new Set(Array.isArray(config.closedDates) ? config.closedDates : []);

  const dateParts = (date) => {
    const parts = new Intl.DateTimeFormat('en-CA', {
      timeZone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hourCycle: 'h23',
      weekday: 'short'
    }).formatToParts(date);

    return parts.reduce((result, part) => {
      if (part.type !== 'literal') result[part.type] = part.value;
      return result;
    }, {});
  };

  const dateKey = (year, month, day) =>
    [year, month, day]
      .map((value, index) => index === 0 ? String(value) : String(value).padStart(2, '0'))
      .join('-');

  const countdownLabel = (parts) => {
    const elapsedSeconds =
      Number(parts.hour) * 3600 +
      Number(parts.minute) * 60 +
      Number(parts.second);
    const remainingMinutes = Math.ceil(
      ((cutoffHour * 3600) - elapsedSeconds) / 60
    );

    if (remainingMinutes <= 0) return '';

    const hours = Math.floor(remainingMinutes / 60);
    const minutes = remainingMinutes % 60;

    if (hours > 0 && minutes > 0) {
      return `${hours} hr${hours === 1 ? '' : 's'} ${minutes} min left`;
    }

    if (hours > 0) {
      return `${hours} hour${hours === 1 ? '' : 's'} left`;
    }

    return `${remainingMinutes} min left`;
  };

  const availabilityLabel = () => {
    const now = new Date();
    const today = dateParts(now);
    const todayKey = dateKey(today.year, today.month, today.day);
    const todayIsOpen = today.weekday !== 'Sun' && !closedDates.has(todayKey);

    if (todayIsOpen && Number(today.hour) < cutoffHour) {
      const countdown = countdownLabel(today);
      return countdown
        ? `Delivery available today · ${countdown}`
        : 'Delivery available today';
    }

    const calendarDate = new Date(Date.UTC(
      Number(today.year),
      Number(today.month) - 1,
      Number(today.day),
      12
    ));

    for (let offset = 1; offset <= 14; offset += 1) {
      const candidate = new Date(calendarDate);
      candidate.setUTCDate(candidate.getUTCDate() + offset);

      const year = candidate.getUTCFullYear();
      const month = candidate.getUTCMonth() + 1;
      const day = candidate.getUTCDate();
      const candidateKey = dateKey(year, month, day);

      if (candidate.getUTCDay() === 0 || closedDates.has(candidateKey)) continue;

      const weekday = new Intl.DateTimeFormat('en-CA', {
        weekday: 'long',
        timeZone: 'UTC'
      }).format(candidate);

      return offset === 1
        ? 'Next delivery: tomorrow'
        : `Next delivery: ${weekday}`;
    }

    return 'Delivery date available at checkout';
  };

  const updateMessage = (message, label) => {
    const text = message.querySelector('.bb-delivery-text');
    if (text && text.textContent !== label) text.textContent = label;
    if (message.getAttribute('aria-label') !== label) {
      message.setAttribute('aria-label', label);
    }
  };

  const makeMessage = (className, label) => {
    const message = document.createElement('div');
    message.className = className;

    const dot = document.createElement('span');
    dot.className = 'bb-delivery-dot';
    dot.setAttribute('aria-hidden', 'true');

    const text = document.createElement('span');
    text.className = 'bb-delivery-text';

    message.append(dot, text);
    updateMessage(message, label);
    return message;
  };

  const removeImageBadges = () => {
    document.querySelectorAll('.bb-delivery-pill').forEach((badge) => badge.remove());
    document.querySelectorAll('.bb-delivery-badge-host')
      .forEach((host) => host.classList.remove('bb-delivery-badge-host'));
  };

  const messagesOwnedBy = (host) =>
    Array.from(host.querySelectorAll('.bb-card-delivery')).filter((message) =>
      message.closest('product-card, .product-card-basic') === host
    );

  const keepOneMessage = (messages, label) => {
    const message = messages.shift() || null;
    messages.forEach((duplicate) => duplicate.remove());
    if (message) updateMessage(message, label);
    return message;
  };

  const addCardMessage = (card, label) => {
    if (!card) return;

    const existingMessages = messagesOwnedBy(card);
    if (card.dataset.productAvailable !== 'true') {
      existingMessages.forEach((message) => message.remove());
      return;
    }

    if (keepOneMessage(existingMessages, label)) return;

    const message = makeMessage('bb-card-delivery', label);
    const footer = card.querySelector('.product-grid--footer');
    const quickAdd = footer && footer.querySelector('.quick-add-wrapper');

    if (quickAdd) {
      footer.insertBefore(message, quickAdd);
      return;
    }

    const price = card.querySelector('.product-grid--price');
    if (price) {
      price.insertAdjacentElement('afterend', message);
      return;
    }

    const content = card.querySelector(':scope > div');
    if (content) content.appendChild(message);
  };

  const addBasicCardMessage = (card, label) => {
    if (!card) return;

    const existingMessages = messagesOwnedBy(card);
    if (keepOneMessage(existingMessages, label)) return;

    const footer = card.querySelector('.product-card-basic--info--footer');
    if (footer) footer.prepend(makeMessage('bb-card-delivery', label));
  };

  const addProductMessage = (label) => {
    const productRoot = document.querySelector('.template-product .product-single-wrapper');
    if (!productRoot) return;

    const wrappers = Array.from(
      productRoot.querySelectorAll('product-form .paymentButtonsWrapper')
    );
    const wrapper = wrappers.find((item) => item.offsetParent !== null) || wrappers[0];
    const messages = Array.from(productRoot.querySelectorAll('.bb-product-delivery'));
    const message = keepOneMessage(messages, label);

    if (!wrapper) {
      if (message) message.remove();
      return;
    }

    const deliveryMessage = message || makeMessage('bb-product-delivery', label);
    if (deliveryMessage.nextElementSibling !== wrapper) {
      wrapper.insertAdjacentElement('beforebegin', deliveryMessage);
    }
  };

  let observer;
  let scheduled = false;

  const observe = () => {
    if (!observer) return;
    observer.observe(document.documentElement, {
      childList: true,
      subtree: true
    });
  };

  const applyMessages = () => {
    if (observer) observer.disconnect();

    const label = availabilityLabel();
    removeImageBadges();

    document.querySelectorAll('.bb-card-delivery').forEach((message) => {
      if (!message.closest('product-card, .product-card-basic')) message.remove();
    });

    const isHomePage =
      document.body.classList.contains('template-index') ||
      window.location.pathname === '/';

    if (isHomePage) {
      document.querySelectorAll('.bb-card-delivery')
        .forEach((message) => message.remove());
    } else {
      document.querySelectorAll('product-card')
        .forEach((card) => addCardMessage(card, label));

      document.querySelectorAll('.product-card-basic')
        .forEach((card) => addBasicCardMessage(card, label));
    }

    if (config.productAvailable !== false) {
      addProductMessage(label);
    } else {
      document.querySelectorAll('.template-product .bb-product-delivery')
        .forEach((message) => message.remove());
    }

    observe();
  };

  const scheduleApply = () => {
    if (scheduled) return;
    scheduled = true;
    window.requestAnimationFrame(() => {
      scheduled = false;
      applyMessages();
    });
  };

  observer = new MutationObserver(scheduleApply);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', applyMessages, { once: true });
  } else {
    applyMessages();
  }

  window.setInterval(scheduleApply, 30000);
})();